<template>
  <AppCard no-padding>
    <template #header>
      <div class="flex flex-1 items-center justify-between">
        <div
          class="text-navy-800 flex items-center gap-2 text-[13px] font-semibold"
        >
          <i class="pi pi-server text-navy-400 text-[13px]" />
          Agents
          <span
            class="bg-navy-50 text-navy-500 min-w-[20px] rounded-full px-[7px] py-px text-center text-[11px] font-semibold"
          >
            {{ agents.length }}
          </span>
        </div>
        <AppButton
          v-if="isAdmin && agents.length > 0"
          variant="ghost"
          size="sm"
          @click="$emit('setup')"
        >
          + Add Agent
        </AppButton>
      </div>
    </template>

    <!-- Empty state -->
    <div
      v-if="agents.length === 0"
      class="flex flex-col items-center px-5 py-9 text-center"
    >
      <div
        class="mb-3 flex h-[52px] w-[52px] items-center justify-center rounded-xl bg-neutral-100"
      >
        <i class="pi pi-server text-[22px] text-neutral-400" />
      </div>
      <p class="text-navy-800 mb-1 text-sm font-semibold">
        No agents connected
      </p>
      <p class="mx-auto mb-4 max-w-[340px] text-[13px] text-neutral-500">
        Connect an agent to enable importing orders and documents from your
        title production system.
      </p>
      <AppButton v-if="isAdmin" @click="$emit('setup')">
        Set Up Agent
      </AppButton>
    </div>

    <!-- Agent list -->
    <template v-else>
      <div
        v-for="(agent, i) in agents"
        :key="agent.id"
        class="flex items-center justify-between px-5 py-3.5 transition-colors hover:bg-neutral-50"
        :class="{ 'border-b border-neutral-100': i < agents.length - 1 }"
      >
        <div class="flex items-center gap-3">
          <!-- Status icon -->
          <div
            class="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] border"
            :class="
              agent.status === 'connected'
                ? 'border-green-200 bg-green-50'
                : 'border-neutral-200 bg-neutral-100'
            "
          >
            <i
              class="pi pi-server text-base"
              :class="
                agent.status === 'connected'
                  ? 'text-green-600'
                  : 'text-neutral-400'
              "
            />
          </div>

          <div>
            <div class="text-navy-800 text-sm font-semibold">
              {{ agent.name || "Unnamed Agent" }}
            </div>
            <div
              class="mt-0.5 flex items-center gap-1.5 text-xs text-neutral-500"
            >
              <span>{{ agent.hostname || "---" }}</span>
              <span class="text-neutral-300">&middot;</span>
              <span>v{{ agent.agentVersion || "---" }}</span>
              <template v-if="isOutdated(agent.agentVersion)">
                <span class="text-neutral-300">&middot;</span>
                <a
                  :href="downloadUrl()"
                  class="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700"
                >
                  <i class="pi pi-arrow-up text-[10px]" />
                  v{{ latestVersion }} available
                </a>
              </template>
              <span class="text-neutral-300">&middot;</span>
              <span>{{ formatHeartbeat(agent.lastHeartbeatAt) }}</span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2.5">
          <StatusBadge :variant="statusVariant(agent.status)" dot>
            {{ statusLabel(agent.status) }}
          </StatusBadge>
          <button
            v-if="isAdmin"
            class="shrink-0 cursor-pointer rounded px-2 py-1 text-xs font-medium text-green-600 transition-colors hover:bg-green-50"
            @click="$emit('manage', agent)"
          >
            Manage &rarr;
          </button>
        </div>
      </div>
    </template>
  </AppCard>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue"
import type { AgentRegistrationType } from "../../pages/Company/types"
import AppButton from "../ui/AppButton.vue"
import AppCard from "../ui/AppCard.vue"
import StatusBadge from "../ui/StatusBadge.vue"

interface Props {
  agents: AgentRegistrationType[]
  isAdmin: boolean
  companyId: string
}

const props = defineProps<Props>()

const latestVersion = ref<string | null>(null)

const fetchLatestVersion = async () => {
  try {
    const resp = await fetch(
      `/api/agent/installer/version?company_id=${props.companyId}`,
    )
    if (resp.ok) {
      const data = await resp.json()
      latestVersion.value = data.version ?? null
    }
  } catch {
    // Best-effort
  }
}

const isOutdated = (agentVersion: string | null | undefined) => {
  if (!agentVersion || !latestVersion.value) return false
  return agentVersion !== latestVersion.value
}

const downloadUrl = () => `/api/agent/installer?company_id=${props.companyId}`

onMounted(() => {
  void fetchLatestVersion()
})

defineEmits<{
  setup: []
  manage: [agent: AgentRegistrationType]
}>()

const statusVariant = (status: string) => {
  if (status === "connected") return "success"
  if (status === "disconnected") return "warning"
  return "info"
}

const statusLabel = (status: string) => {
  const labels: Record<string, string> = {
    connected: "Connected",
    disconnected: "Disconnected",
    pairing: "Pairing",
    configuring: "Configuring",
  }
  return labels[status] || status
}

const formatHeartbeat = (isoString: string | null | undefined) => {
  if (!isoString) return "---"
  const date = new Date(isoString)
  const diffMs = Date.now() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  if (diffSecs < 60) return `${diffSecs}s ago`
  const diffMins = Math.floor(diffSecs / 60)
  if (diffMins < 60) return `${diffMins}m ago`
  return date.toLocaleString()
}
</script>
